import React, { useEffect, useLayoutEffect, useState } from 'react';
import './Walkthrough.css';

const VIEWPORT_PADDING = 16;
const CARD_GAP = 18;
const CARD_WIDTH = 340;

const keepInsideViewport = (value, minimum, maximum) => {
  return Math.min(Math.max(value, minimum), maximum);
};

const Walkthrough = ({
  steps = [],
  stepIndex = 0,
  onStepChange,
  onClose,
  onFinish,
}) => {
    const [targetRects, setTargetRects] = useState([]);

  const [cardPosition, setCardPosition] = useState({
    top: VIEWPORT_PADDING,
    left: VIEWPORT_PADDING,
  });

  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  const calculatePositions = () => {
    if (!currentStep) return;

    const selectors = Array.isArray(currentStep.selectors)
  ? currentStep.selectors
  : currentStep.selector
    ? [currentStep.selector]
    : [];

const targets = selectors
  .map((selector) => document.querySelector(selector))
  .filter(Boolean);

if (targets.length === 0) {
  setTargetRects([]);

  setCardPosition({
    top: Math.max(
      VIEWPORT_PADDING,
      window.innerHeight / 2 - 160
    ),
    left: Math.max(
      VIEWPORT_PADDING,
      window.innerWidth / 2 - CARD_WIDTH / 2
    ),
  });

  return;
}

const padding =
  currentStep.highlightPadding !== undefined
    ? currentStep.highlightPadding
    : 8;

const nextTargetRects = targets.map((target) => {
  const rect = target.getBoundingClientRect();

  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    right: rect.right + padding,
    bottom: rect.bottom + padding,
  };
});

setTargetRects(nextTargetRects);

    const placement = currentStep.placement || 'bottom';

    let top;
    let left;

    if (placement === 'canvas-center') {
    const workspace =
        document.querySelector('[data-tour="editor-workspace"]');

    if (workspace) {
        const workspaceRect = workspace.getBoundingClientRect();

        top =
        workspaceRect.top +
        workspaceRect.height / 2 -
        160;

        left =
        workspaceRect.left +
        workspaceRect.width / 2 -
        CARD_WIDTH / 2;
    } else {
        top = window.innerHeight / 2 - 160;
        left = window.innerWidth / 2 - CARD_WIDTH / 2;
    }
    } else {
    const primaryRect = nextTargetRects[0];

    top = primaryRect.bottom + CARD_GAP;

    left =
        primaryRect.left +
        primaryRect.width / 2 -
        CARD_WIDTH / 2;

    if (placement === 'top') {
        top = primaryRect.top - 250 - CARD_GAP;
    }

    if (placement === 'top-wide-gap') {
        top = primaryRect.top - 290 - 28;
    }

    if (placement === 'right') {
        top = primaryRect.top;
        left = primaryRect.right + CARD_GAP;
    }

    if (placement === 'left') {
        top = primaryRect.top;
        left = primaryRect.left - CARD_WIDTH - CARD_GAP;
    }

    if (placement === 'center') {
        top = window.innerHeight / 2 - 160;
        left = window.innerWidth / 2 - CARD_WIDTH / 2;
    }
    }

    const maximumLeft =
    window.innerWidth - CARD_WIDTH - VIEWPORT_PADDING;

    const maximumTop =
    window.innerHeight - 290 - VIEWPORT_PADDING;

    setCardPosition({
    top: keepInsideViewport(
        top,
        VIEWPORT_PADDING,
        Math.max(VIEWPORT_PADDING, maximumTop)
    ),
    left: keepInsideViewport(
        left,
        VIEWPORT_PADDING,
        Math.max(VIEWPORT_PADDING, maximumLeft)
    ),
    });
  };

    useLayoutEffect(() => {
        if (currentStep?.onEnter) {
            currentStep.onEnter();
        }

        calculatePositions();

        const delayedCalculation = window.setTimeout(() => {
            calculatePositions();
        }, 150);

        return () => {
            window.clearTimeout(delayedCalculation);
        };

        // Run only when the user actually changes walkthrough steps.
        // currentStep is deliberately excluded because its object is recreated
        // whenever MainApp renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [stepIndex]);

  useEffect(() => {
    const handleWindowChange = () => {
        calculatePositions();
    };

    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);

    return () => {
        window.removeEventListener('resize', handleWindowChange);
        window.removeEventListener('scroll', handleWindowChange, true);
    };

    // currentStep is intentionally excluded for the same reason as above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepIndex]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (onClose) {
          onClose();
        }
      }

      if (event.key === 'ArrowRight') {
        if (isLastStep) {
          if (onFinish) {
            onFinish();
          }
        } else if (onStepChange) {
          onStepChange(stepIndex + 1);
        }
      }

      if (event.key === 'ArrowLeft' && stepIndex > 0) {
        if (onStepChange) {
          onStepChange(stepIndex - 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    stepIndex,
    isLastStep,
    onClose,
    onFinish,
    onStepChange,
  ]);

  if (!currentStep) return null;

  const handleNext = () => {
    if (isLastStep) {
      if (onFinish) {
        onFinish();
      }

      return;
    }

    if (onStepChange) {
      onStepChange(stepIndex + 1);
    }
  };

  const handleBack = () => {
    if (stepIndex === 0) return;

    if (onStepChange) {
      onStepChange(stepIndex - 1);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div
      className="walkthrough-layer"
      role="dialog"
      aria-modal="true"
    >
      {targetRects.length > 1 ? (
        <>
            <svg
            className="walkthrough-multi-overlay"
            width="100%"
            height="100%"
            viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            >
            <defs>
                <mask id={`walkthrough-mask-${stepIndex}`}>
                <rect
                    x="0"
                    y="0"
                    width={window.innerWidth}
                    height={window.innerHeight}
                    fill="white"
                />

                {targetRects.map((rect, index) => (
                    <rect
                    key={`mask-hole-${stepIndex}-${index}`}
                    x={rect.left}
                    y={rect.top}
                    width={rect.width}
                    height={rect.height}
                    rx="12"
                    ry="12"
                    fill="black"
                    />
                ))}
                </mask>
            </defs>

            <rect
                x="0"
                y="0"
                width={window.innerWidth}
                height={window.innerHeight}
                fill="rgba(29, 22, 13, 0.68)"
                mask={`url(#walkthrough-mask-${stepIndex})`}
            />
            </svg>

            {targetRects.map((rect, index) => (
            <div
                key={`highlight-${stepIndex}-${index}`}
                className="walkthrough-highlight walkthrough-highlight-multi"
                style={{
                top: `${rect.top}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                height: `${rect.height}px`,
                }}
            />
            ))}
        </>
        ) : targetRects.length === 1 ? (
        <div
            className="walkthrough-highlight walkthrough-highlight-single"
            style={{
            top: `${targetRects[0].top}px`,
            left: `${targetRects[0].left}px`,
            width: `${targetRects[0].width}px`,
            height: `${targetRects[0].height}px`,
            }}
        />
        ) : (
        <div className="walkthrough-full-overlay" />
        )}

      <div
        key={`walkthrough-card-${stepIndex}`}
        className="walkthrough-card walkthrough-card-enter"
        style={{
            top: `${cardPosition.top}px`,
            left: `${cardPosition.left}px`,
            width: `${CARD_WIDTH}px`,
        }}
        >
        <div className="walkthrough-card-top">
          <div className="walkthrough-progress">
            {stepIndex + 1} of {steps.length}
          </div>

          <button
            type="button"
            className="walkthrough-close-button"
            onClick={handleClose}
            aria-label="Close walkthrough"
            title="Skip walkthrough"
          >
            ×
          </button>
        </div>

        {currentStep.eyebrow && (
          <div className="walkthrough-eyebrow">
            {currentStep.eyebrow}
          </div>
        )}

        <h2 className="walkthrough-title">
          {currentStep.title}
        </h2>

        <div className="walkthrough-description">
          {currentStep.description}
        </div>

        <div className="walkthrough-progress-track">
          <div
            className="walkthrough-progress-fill"
            style={{
              width: `${
                ((stepIndex + 1) / steps.length) * 100
              }%`,
            }}
          />
        </div>

        <div className="walkthrough-actions">
          <button
            type="button"
            className="walkthrough-secondary-button"
            onClick={handleClose}
          >
            Skip Tour
          </button>

          <div className="walkthrough-navigation-buttons">
            {stepIndex > 0 && (
              <button
                type="button"
                className="walkthrough-back-button"
                onClick={handleBack}
              >
                Back
              </button>
            )}

            <button
              type="button"
              className="walkthrough-primary-button"
              onClick={handleNext}
            >
              {currentStep.nextLabel ||
                (isLastStep ? 'Finish' : 'Next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Walkthrough;